import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, Mail, Phone, Search, Plus, Minus, Book } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';

const SupportPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simuler l'envoi du formulaire
    toast.success('Votre message a été envoyé avec succès!');
    setName('');
    setEmail('');
    setMessage('');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  const faqItems = [
    {
      question: "Comment puis-je créer un compte sur PersonalTube?",
      answer: "Pour créer un compte, cliquez sur le bouton 'Commencer maintenant' en haut à droite de la page d'accueil. Remplissez le formulaire d'inscription avec votre adresse e-mail et un mot de passe sécurisé. Une fois inscrit, vous pourrez accéder à toutes les fonctionnalités de PersonalTube."
    },
    {
      question: "Comment puis-je ajouter des vidéos à mes collections?",
      answer: "Pour ajouter une vidéo à vos collections, naviguez vers la vidéo que vous souhaitez sauvegarder. Sous la vidéo, cliquez sur l'icône 'Ajouter à une collection'. Vous pouvez alors choisir une collection existante ou en créer une nouvelle. La vidéo sera automatiquement ajoutée à votre collection."
    },
    {
      question: "Est-ce que PersonalTube est gratuit?",
      answer: "Oui, PersonalTube propose une version gratuite avec des fonctionnalités de base. Nous proposons également des forfaits premium qui offrent des fonctionnalités supplémentaires comme le téléchargement de vidéos, l'absence de publicités et un stockage illimité pour vos collections."
    },
    {
      question: "Comment puis-je suivre mes créateurs préférés?",
      answer: "Pour suivre un créateur, visitez sa page de profil et cliquez sur le bouton 'Suivre'. Vous recevrez des notifications lorsque ce créateur publie du nouveau contenu, et ses vidéos apparaîtront dans votre flux personnalisé."
    },
    {
      question: "Comment puis-je récupérer mon mot de passe?",
      answer: "Si vous avez oublié votre mot de passe, cliquez sur 'Se connecter', puis sur 'Mot de passe oublié?'. Saisissez l'adresse e-mail associée à votre compte et nous vous enverrons un lien pour réinitialiser votre mot de passe."
    },
    {
      question: "Comment puis-je contacter le support technique?",
      answer: "Vous pouvez contacter notre équipe de support technique en utilisant le formulaire de contact ci-dessous. Nous nous efforçons de répondre à toutes les demandes dans un délai de 24 heures ouvrables."
    }
  ];

  const filteredFaqs = searchTerm 
    ? faqItems.filter(item => 
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
        item.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : faqItems;

  return (
    <div className="min-h-screen flex flex-col bg-youtube-light dark:bg-youtube-dark">
      <Navbar />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-youtube-red to-red-700 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Centre de Support PersonalTube
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl max-w-3xl mx-auto mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              Nous sommes là pour vous aider à tirer le meilleur parti de votre expérience
            </motion.p>
            
            <motion.div 
              className="flex flex-col md:flex-row gap-4 justify-center max-w-3xl mx-auto relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <Input
                type="text"
                placeholder="Rechercher dans notre base de connaissances..."
                className="flex-1 h-14 text-black dark:text-white rounded-full px-6 shadow-lg focus:ring-2 focus:ring-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="youtube" size="lg" className="h-14 px-8 rounded-full">
                <Search className="mr-2" />
                Rechercher
              </Button>
            </motion.div>
          </div>
        </section>
        
        {/* Support Cards */}
        <section className="py-16 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4">
            <motion.h2 
              className="text-3xl font-bold text-center mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              Comment pouvons-nous vous aider?
            </motion.h2>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants}>
                <Card className="h-full hover:shadow-xl transition-shadow duration-300">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-youtube-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <HelpCircle className="w-8 h-8 text-youtube-red" />
                    </div>
                    <CardTitle>FAQ</CardTitle>
                    <CardDescription>Trouvez rapidement des réponses à vos questions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-center text-gray-600 dark:text-gray-400">
                      Consultez notre base de connaissances pour des réponses aux questions les plus fréquentes.
                    </p>
                  </CardContent>
                  <CardFooter className="flex justify-center">
                    <Button variant="youtubeOutline" className="w-full">
                      Voir toutes les FAQs
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Card className="h-full hover:shadow-xl transition-shadow duration-300">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-youtube-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-youtube-red" />
                    </div>
                    <CardTitle>Contact</CardTitle>
                    <CardDescription>Envoyez-nous un message</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-center text-gray-600 dark:text-gray-400">
                      Vous ne trouvez pas la réponse que vous cherchez? Notre équipe de support est là pour vous aider.
                    </p>
                  </CardContent>
                  <CardFooter className="flex justify-center">
                    <Button variant="youtubeOutline" className="w-full">
                      Nous contacter
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Card className="h-full hover:shadow-xl transition-shadow duration-300">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-youtube-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Book className="w-8 h-8 text-youtube-red" />
                    </div>
                    <CardTitle>Documentation</CardTitle>
                    <CardDescription>Guides et ressources détaillées</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-center text-gray-600 dark:text-gray-400">
                      Explorez notre documentation complète pour maîtriser toutes les fonctionnalités de PersonalTube.
                    </p>
                  </CardContent>
                  <CardFooter className="flex justify-center">
                    <Link to="/documentation" className="w-full">
                      <Button variant="youtubeOutline" className="w-full">
                        Voir la documentation
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <motion.h2 
              className="text-3xl font-bold text-center mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              Questions fréquemment posées
            </motion.h2>
            
            {searchTerm && filteredFaqs.length === 0 ? (
              <Alert className="mb-6">
                <HelpCircle className="h-5 w-5" />
                <AlertTitle>Aucun résultat trouvé</AlertTitle>
                <AlertDescription>
                  Aucune FAQ ne correspond à votre recherche. Essayez avec d'autres termes ou contactez-nous directement.
                </AlertDescription>
              </Alert>
            ) : null}
            
            <Accordion type="single" collapsible className="w-full">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <AccordionItem value={`item-${index}`}>
                    <AccordionTrigger className="text-left py-4 text-lg font-medium hover:text-youtube-red transition-colors">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 dark:text-gray-400 text-base leading-relaxed p-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </section>
        
        {/* Contact Form */}
        <section className="py-16 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-3xl mx-auto px-4">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold mb-4">Contactez-nous</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Une question spécifique? N'hésitez pas à nous contacter directement.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium">
                      Nom complet
                    </label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Votre nom"
                      required
                      className="w-full"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium">
                      Adresse e-mail
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      required
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-medium">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Comment pouvons-nous vous aider?"
                    required
                    className="w-full min-h-[150px]"
                  />
                </div>
                
                <div className="flex justify-end">
                  <Button type="submit" variant="youtube" size="youtube" className="px-8">
                    Envoyer le message
                  </Button>
                </div>
              </form>
            </motion.div>
            
            <motion.div 
              className="mt-12 flex flex-col md:flex-row items-center justify-center gap-8 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-youtube-red/10 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-youtube-red" />
                </div>
                <span className="text-gray-600 dark:text-gray-400">support@personaltube.com</span>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-youtube-red/10 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-youtube-red" />
                </div>
                <span className="text-gray-600 dark:text-gray-400">+33 (0)1 23 45 67 89</span>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p>© {new Date().getFullYear()} PersonalTube. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default SupportPage;

